<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <ion-item
    class="user-card-item ion-no-padding"
    :detail="false"
    :class="{
      selected: user.isSelected,
      revoked: user.isRevoked(),
      suspended: user.isFrozen(),
      'current-user': user.isCurrent,
      'no-padding-end': !user.isSelected,
      'user-card-item--hovered': !user.isSelected && (menuOpened || isHovered),
    }"
    @click="allowSelection && $emit('select', user, !user.isSelected)"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @contextmenu="onOptionsClick"
  >
    <div
      class="card-checkbox"
      v-if="!user.isRevoked()"
    >
      <ms-checkbox
        :checked="user.isSelected"
        v-if="allowSelection && (user.isSelected || isHovered || activeCheckbox) && !user.isCurrent"
        @click.stop
        @change="allowSelection && $emit('select', user, $event)"
      />
      <ms-image
        v-if="user.isCurrent"
        :image="LockClosedIcon"
        class="lock-icon"
      />
    </div>
    <div
      class="card-option"
      v-show="isHovered || menuOpened"
      @click.stop="onOptionsClick($event)"
    >
      <ion-icon :icon="ellipsisHorizontal" />
    </div>
    <div class="user-card">
      <user-avatar-name
        class="user-card-avatar medium"
        :user-avatar="user.humanHandle.label"
      />
      <div class="user-card-info">
        <ion-text
          class="user-card-info__name body"
          :title="user.humanHandle.label"
        >
          <span>{{ user.humanHandle.label }}</span>
          <span
            v-if="user.isCurrent"
            class="body user-name--self"
          >
            {{ $msTranslate('UsersPage.currentUser') }}
          </span>
        </ion-text>
        <ion-text
          class="user-card-info__email label-email body-sm"
          :title="user.humanHandle.email"
          @click.stop="onCopyEmailClicked(user.humanHandle.email)"
        >
          <span class="label-email__text">{{ user.humanHandle.email }}</span>
          <ion-icon
            v-if="!user.isCurrent"
            :icon="emailCopied ? checkmark : copy"
            class="email-copy-icon"
            :class="{ 'email-copy-icon--copied': emailCopied }"
          />
        </ion-text>
      </div>
      <div class="user-card-profile">
        <user-profile-tag :profile="user.currentProfile" />
        <div
          v-if="!user.isActive()"
          class="user-revoked user-suspended"
        >
          <user-status-tag
            :revoked="user.isRevoked()"
            :frozen="user.isFrozen()"
            :show-tooltip="true"
          />
        </div>
      </div>
      <div class="user-card-join">
        <ion-text class="user-card-join-label body-sm">
          {{ $msTranslate(formatTimeSince(user.createdOn, '--', 'short')) }}
        </ion-text>
      </div>
    </div>
  </ion-item>
</template>

<script setup lang="ts">
import { copyToClipboard } from '@/common/clipboard';
import UserAvatarName from '@/components/users/UserAvatarName.vue';
import UserProfileTag from '@/components/users/UserProfileTag.vue';
import UserStatusTag from '@/components/users/UserStatusTag.vue';
import { UserModel } from '@/components/users/types';
import { InformationManager, InformationManagerKey } from '@/services/informationManager';
import { IonIcon, IonItem, IonText } from '@ionic/vue';
import { checkmark, copy, ellipsisHorizontal } from 'ionicons/icons';
import { formatTimeSince, LockClosedIcon, MsCheckbox, MsImage } from 'megashark-lib';
import { inject, ref, Ref } from 'vue';

const isHovered = ref(false);
const menuOpened = ref(false);
const emailCopied = ref(false);
const informationManager: Ref<InformationManager> = inject(InformationManagerKey)!;

const emits = defineEmits<{
  (e: 'click', event: Event, user: UserModel): void;
  (e: 'menuClick', event: Event, user: UserModel, onFinished: () => void): void;
  (e: 'select', user: UserModel, selected: boolean): void;
}>();

const props = defineProps<{
  user: UserModel;
  activeCheckbox: boolean;
  allowSelection: boolean;
}>();

defineExpose({
  isHovered,
});

async function onOptionsClick(event: Event): Promise<void> {
  event.preventDefault();
  event.stopPropagation();
  menuOpened.value = true;
  emits('menuClick', event, props.user, () => {
    menuOpened.value = false;
  });
}

async function onCopyEmailClicked(email: string): Promise<void> {
  emailCopied.value = true;

  setTimeout(() => {
    emailCopied.value = false;
  }, 5000);

  await copyToClipboard(email, informationManager.value, 'UsersPage.copyEmail.success', 'UsersPage.copyEmail.failed');
}
</script>

<style scoped lang="scss">
.user-card-item {
  max-width: 15rem;
  width: 100%;
  height: fit-content;

  &:hover:not(.revoked) {
    background: var(--parsec-color-light-primary-30);

    .user-card-info__email {
      color: var(--parsec-color-light-secondary-text);
    }
  }

  &.selected:not(.revoked) {
    .user-card-info__name {
      color: var(--parsec-color-light-primary-700);
    }

    .user-card-info__email {
      color: var(--parsec-color-light-secondary-text);
    }
  }

  &.current-user {
    --background: var(--parsec-color-light-secondary-background);

    .lock-icon {
      width: 1.25rem;
      --fill-color: var(--parsec-color-light-secondary-grey);
    }
  }

  .user-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 0.5rem 1rem 1rem;
    width: 100%;
    margin: auto;
    color: var(--parsec-color-light-secondary-text);

    &-info {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      &__name {
        color: var(--parsec-color-light-secondary-text);
        display: flex;
        gap: 0.5rem;

        span {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        .user-name--self {
          color: var(--parsec-color-light-secondary-grey);
        }
      }

      &__email {
        color: var(--parsec-color-light-secondary-grey);
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        display: flex;
        align-items: center;
        cursor: pointer;
        gap: 0.125rem;

        .label-email {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;

          &__text {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }
        }

        .email-copy-icon {
          display: none;
          color: var(--parsec-color-light-secondary-soft-grey);
          padding: 0 0.375rem;
          border-radius: var(--parsec-radius-6);
          font-size: 1rem;
          cursor: pointer;
          color: var(--parsec-color-light-secondary-grey);
          flex-shrink: 0;

          &--copied {
            color: var(--parsec-color-light-success-700) !important;
            display: flex !important;
          }
        }
      }
    }

    &-profile {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }

  &.user-card-item--hovered {
    .email-copy-icon {
      display: flex;
    }
    .label-email:hover {
      color: var(--parsec-color-light-primary-600);

      .email-copy-icon {
        color: var(--parsec-color-light-primary-600);
      }
    }
  }
}

.card-checkbox {
  top: 1rem;
  right: 1rem;
}

.card-option {
  padding: 1rem;
  right: 0;
  bottom: 0;
}
</style>
